/**
 * Página de produto: ao trocar as opções (cor, tamanho, kit...) atualiza a variante
 * selecionada, o preço, o "% OFF", o parcelamento, o botão de compra, a URL e a imagem da galeria.
 * Funciona com opções em lista suspensa, botões ou cards.
 * Sem este script o formulário continua funcionando (adiciona a variante inicial).
 */
(() => {
  const initProduct = (root) => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';

    // A oferta única do Seletor de variantes traz o próprio campo de quantidade; se a página
    // também tiver o bloco Quantidade, fica só o do bloco (dois campos iguais confundiriam o carrinho).
    const offerQuantity = root.querySelectorAll('[data-offer-quantity]');
    if (offerQuantity.length && root.querySelectorAll('input[name="quantity"]').length > offerQuantity.length) {
      offerQuantity.forEach((el) => el.remove());
    }

    // "Preço total" (bloco Botões de compra): variante x quantidade + itens marcados no
    // "compre junto". Fica declarado aqui em cima (antes do bloco do compre-junto) pra que os
    // dois lados — a troca de variante/quantidade lá embaixo e a marcação de itens aqui do lado —
    // consigam chamar a mesma função, em vez de cada um manter sua própria soma isolada.
    const totalPriceEls = root.querySelectorAll('[data-total-price]');
    const summaryEl = root.querySelector('[data-order-summary]');
    const totalCurrency = root.dataset.currency || 'BRL';
    const totalFormat = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: totalCurrency });
    // Valor da variante atual em centavos: do resumo, ou (sem resumo na página) do JSON de variantes
    const baseAmountFromJson = () => {
      try {
        const list = JSON.parse((root.querySelector('[data-variants]') || {}).textContent || '[]');
        const idInput = root.querySelector('form.product__form [name="id"], form[action*="/cart/add"] [name="id"]');
        const current = list.find((v) => idInput && String(v.id) === String(idInput.value)) || list[0];
        return current ? Number(current.amount) || 0 : 0;
      } catch (error) {
        return 0;
      }
    };
    // Resumo da oferta única (produto sem opções): economia e total deste produto x quantidade
    root.querySelectorAll('[data-single-offer]').forEach((card) => {
      const price = Number(card.dataset.price) || 0;
      const compare = Number(card.dataset.compare) || 0;
      const subtotalEl = card.querySelector('[data-offer-subtotal]');
      const savingEl = card.querySelector('[data-offer-saving]');
      const renderOffer = () => {
        const input = root.querySelector('input[name="quantity"]');
        const quantity = Math.max(1, Number(input ? input.value : 1) || 1);
        if (subtotalEl) subtotalEl.textContent = totalFormat.format((price * quantity) / 100);
        if (savingEl && compare > price) savingEl.textContent = totalFormat.format(((compare - price) * quantity) / 100);
      };
      root.addEventListener('input', (event) => {
        if (event.target.matches('input[name="quantity"]')) renderOffer();
      });
      renderOffer();
    });

    let currentVariantAmount = (summaryEl && Number(summaryEl.dataset.baseAmount)) || baseAmountFromJson();
    let currentVariantCompareAmount = summaryEl ? Number(summaryEl.dataset.baseCompareAmount) || currentVariantAmount : currentVariantAmount;
    let currentVariantTitle = summaryEl ? summaryEl.dataset.baseTitle || '' : '';

    const crossSellSubtotal = () => {
      let sum = 0;
      root.querySelectorAll('.crosssell__card[data-price]').forEach((card) => {
        sum += (Number(card.dataset.qty) || 0) * Number(card.dataset.price);
      });
      return sum;
    };

    // Soma o valor ORIGINAL (compare_at, ou o próprio preço quando não há desconto) dos itens
    // marcados no "compre junto" — usado só pela linha "Valor dos produtos" do resumo da compra.
    const crossSellOriginalSubtotal = () => {
      let sum = 0;
      root.querySelectorAll('.crosssell__card[data-price]').forEach((card) => {
        const quantity = Number(card.dataset.qty) || 0;
        const compare = Number(card.dataset.compare) || Number(card.dataset.price) || 0;
        sum += quantity * compare;
      });
      return sum;
    };

    const crossSellNames = () => {
      const names = [];
      root.querySelectorAll('.crosssell__card[data-price]').forEach((card) => {
        if ((Number(card.dataset.qty) || 0) > 0 && card.dataset.name) names.push(card.dataset.name);
      });
      return names;
    };

    // Barra "faltam R$ X para o frete grátis": usa o mesmo total do resumo (variante x quantidade +
    // compre junto), então reage a quantidade e itens extras também em produto sem variantes.
    const updateShippingBar = (total) => {
      root.querySelectorAll('[data-shipping-bar]').forEach((el) => {
        const threshold = Number(el.dataset.shippingThreshold);
        if (!threshold || !total) return;
        const label = el.querySelector('[data-shipping-label]');
        const fill = el.querySelector('[data-shipping-fill]');
        if (total >= threshold) {
          if (label) label.textContent = el.dataset.shippingReachedText || '';
          if (fill) fill.style.width = '100%';
          return;
        }
        if (fill) fill.style.width = `${Math.round((total / threshold) * 100)}%`;
        if (label) {
          const currency = el.dataset.shippingCurrency || 'BRL';
          const format = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency });
          label.textContent = (el.dataset.shippingMissingText || '').replace('[valor]', format.format((threshold - total) / 100));
        }
      });
    };

    const updateTotalPrice = () => {
      const quantityInput = root.querySelector('input[name="quantity"]');
      const quantity = Math.max(1, Number(quantityInput ? quantityInput.value : 1) || 1);
      const total = currentVariantAmount * quantity + crossSellSubtotal();
      updateShippingBar(total);
      if (!totalPriceEls.length && !summaryEl) return;

      totalPriceEls.forEach((el) => {
        el.classList.remove('price--on-sale');
        el.replaceChildren();
        const current = document.createElement('span');
        current.className = 'price__current';
        current.textContent = totalFormat.format(total / 100);
        el.append(current);
      });

      if (!summaryEl) return;

      // Linha "Kit · addons": nome da variante (ou do produto, sem variantes reais) + os itens
      // marcados no compre-junto, na mesma ordem em que aparecem na lista.
      const kitEl = summaryEl.querySelector('[data-summary-kit]');
      if (kitEl) {
        const names = crossSellNames();
        kitEl.textContent = names.length ? `${currentVariantTitle} + ${names.join(', ')}` : currentVariantTitle;
      }

      const originalTotal = currentVariantCompareAmount * quantity + crossSellOriginalSubtotal();
      const discount = Math.max(0, originalTotal - total);
      const cashbackPercent = Number(summaryEl.dataset.cashbackPercent) || 0;
      const cashback = cashbackPercent > 0 ? Math.round((total * cashbackPercent) / 100) : 0;
      const installmentsCount = Number(summaryEl.dataset.installmentsCount) || 0;

      const originalRow = summaryEl.querySelector('[data-summary-original]');
      const originalValue = summaryEl.querySelector('[data-summary-original-value]');
      if (originalRow && originalValue) {
        originalRow.hidden = discount <= 0;
        if (discount > 0) originalValue.textContent = totalFormat.format(originalTotal / 100);
      }

      const discountRow = summaryEl.querySelector('[data-summary-discount]');
      const discountValue = summaryEl.querySelector('[data-summary-discount-value]');
      if (discountRow && discountValue) {
        discountRow.hidden = discount <= 0;
        if (discount > 0) discountValue.textContent = `− ${totalFormat.format(discount / 100)}`;
      }

      const cashbackRow = summaryEl.querySelector('[data-summary-cashback-line]');
      const cashbackValue = summaryEl.querySelector('[data-summary-cashback-value]');
      if (cashbackRow && cashbackValue) {
        cashbackRow.hidden = cashback <= 0;
        if (cashback > 0) cashbackValue.textContent = `+ ${totalFormat.format(cashback / 100)}`;
      }

      // Box "Total" compacto: "Economize X" e o valor do cashback dentro da caixa verde
      const saveEl = summaryEl.querySelector('[data-summary-save]');
      if (saveEl) {
        saveEl.hidden = discount <= 0;
        const saveValue = saveEl.querySelector('[data-summary-save-value]');
        if (saveValue && discount > 0) saveValue.textContent = totalFormat.format(discount / 100);
      }
      root.querySelectorAll('[data-summary-cashback-amount]').forEach((el) => {
        el.textContent = totalFormat.format(cashback / 100);
      });

      const installmentsEl = summaryEl.querySelector('[data-summary-installments]');
      if (installmentsEl) {
        const show = installmentsCount > 1;
        installmentsEl.hidden = !show;
        if (show) {
          const perInstallment = Math.round(total / installmentsCount);
          const template = summaryEl.dataset.installmentsTemplate;
          const perMoney = totalFormat.format(perInstallment / 100);
          if (template) {
            installmentsEl.textContent = template.replace('[n]', String(installmentsCount)).replace('[valor]', perMoney).trim();
          } else {
            const orLabel = summaryEl.dataset.orLabel || '';
            installmentsEl.textContent = `${orLabel} ${installmentsCount}x ${perMoney}`.trim();
          }
        }
      }
    };

    const qtyInputForTotal = root.querySelector('input[name="quantity"]');
    if (qtyInputForTotal) qtyInputForTotal.addEventListener('input', updateTotalPrice);
    updateTotalPrice();

    // "Compre junto": marcar, escolher a quantidade e levar tudo junto com o produto principal
    const crossRoot = root.querySelector('[data-crosssell]');
    const buyForm = root.querySelector('form.product__form');
    if (crossRoot && buyForm) {
      const cards = Array.from(crossRoot.querySelectorAll('.crosssell__card[data-variant-id]'));
      const list = crossRoot.querySelector('[data-crosssell-list]');
      const nav = crossRoot.querySelector('[data-crosssell-nav]');
      const summary = crossRoot.querySelector('[data-crosssell-summary]');
      const maxQuantity = 10;
      const currency = crossRoot.dataset.currency || 'BRL';
      const format = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency });

      const quantityOf = (card) => Number(card.dataset.qty) || 0;

      const render = () => {
        let count = 0;
        let total = 0;

        cards.forEach((card) => {
          const quantity = quantityOf(card);
          const check = card.querySelector('[data-crosssell-check]');
          const counter = card.querySelector('[data-crosssell-count]');
          card.classList.toggle('is-selected', quantity > 0);
          if (check) check.setAttribute('aria-pressed', String(quantity > 0));
          if (counter) counter.textContent = String(quantity || 1);
          count += quantity;
          total += quantity * Number(card.dataset.price);
        });

        if (summary) {
          summary.hidden = count === 0;
          if (count > 0) {
            const countText = count === 1 ? crossRoot.dataset.itemOne : crossRoot.dataset.itemOther.replace('__N__', String(count));
            summary.textContent = crossRoot.dataset.summary.replace('__COUNT__', countText).replace('__TOTAL__', format.format(total / 100));
          }
        }

        updateTotalPrice();
      };

      const setQuantity = (card, quantity) => {
        card.dataset.qty = String(Math.max(0, Math.min(maxQuantity, quantity)));
        render();
      };

      cards.forEach((card) => {
        const on = (selector, handler) => {
          const element = card.querySelector(selector);
          if (element) element.addEventListener('click', handler);
        };
        on('[data-crosssell-add]', () => setQuantity(card, 1));
        on('[data-crosssell-check]', () => setQuantity(card, quantityOf(card) > 0 ? 0 : 1));
        on('[data-crosssell-plus]', () => setQuantity(card, quantityOf(card) + 1));
        on('[data-crosssell-minus]', () => setQuantity(card, quantityOf(card) - 1));
      });

      // Setas da lista: só aparecem quando há mais produtos do que cabem na tela
      if (list && nav) {
        const prev = nav.querySelector('[data-crosssell-prev]');
        const next = nav.querySelector('[data-crosssell-next]');
        const updateNav = () => {
          const overflow = list.scrollWidth > list.clientWidth + 4;
          nav.hidden = !overflow;
          if (prev) prev.disabled = list.scrollLeft <= 2;
          if (next) next.disabled = list.scrollLeft >= list.scrollWidth - list.clientWidth - 2;
        };
        const scrollList = (direction) => list.scrollBy({ left: direction * list.clientWidth * 0.8, behavior: 'smooth' });
        if (prev) prev.addEventListener('click', () => scrollList(-1));
        if (next) next.addEventListener('click', () => scrollList(1));
        list.addEventListener('scroll', updateNav, { passive: true });
        window.addEventListener('resize', updateNav);
        updateNav();
      }

      buyForm.addEventListener('submit', async (event) => {
        const selected = cards.filter((card) => quantityOf(card) > 0);
        if (selected.length === 0) return;

        event.preventDefault();
        const mainId = Number(buyForm.querySelector('[data-variant-id]').value);
        const quantityInput = root.querySelector('input[name="quantity"]');
        const quantity = Math.max(1, Number(quantityInput ? quantityInput.value : 1) || 1);
        const items = [
          { id: mainId, quantity },
          ...selected.map((card) => ({ id: Number(card.dataset.variantId), quantity: quantityOf(card), properties: { _origem: 'compre-junto' } })),
        ];
        const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const button = event.submitter;
        if (button) button.disabled = true;

        let added = false;
        try {
          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          if (!response.ok) throw new Error(String(response.status));
          added = true;
          if (window.bzTrack) window.bzTrack('compre_junto_adicionou', { itens: selected.length, variantes: selected.map((card) => Number(card.dataset.variantId)) });
          // Com carrinho lateral (padrão ou Lumina), abre a gaveta igual à compra normal; sem gaveta, vai para /cart
          if (!window.themeCart || !document.querySelector('[data-cart-drawer]')) throw new Error('sem gaveta');
          await window.themeCart.refresh();
          window.themeCart.open();
        } catch (error) {
          // Já adicionou: só mostra o carrinho. Não adicionou: envia o formulário normal (só o produto principal)
          if (added) window.location.href = `${rootUrl}cart`;
          else buyForm.submit();
        } finally {
          if (button) button.disabled = false;
        }
      });
    }

    // Quantidade em pílula (+/-): variante opcional do bloco Quantidade. Dispara 'input'/'change'
    // no <input> de verdade, então qualquer coisa que já escute a quantidade (ex.: a barra de
    // frete grátis) continua reagindo igual, sem precisar saber qual estilo está em uso.
    root.querySelectorAll('[data-quantity-stepper]').forEach((stepper) => {
      const input = stepper.querySelector('[data-quantity-input]');
      if (!input) return;
      const notify = () => {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      const step = (delta) => {
        const min = Number(input.min) || 1;
        const max = input.max ? Number(input.max) : Infinity;
        const current = Number(input.value) || min;
        input.value = Math.min(max, Math.max(min, current + delta));
        notify();
      };
      const minus = stepper.querySelector('[data-quantity-minus]');
      const plus = stepper.querySelector('[data-quantity-plus]');
      if (minus) minus.addEventListener('click', () => step(-1));
      if (plus) plus.addEventListener('click', () => step(1));
    });

    // Kits em acordeão (Seletor de variantes > "Kits em acordeão"): clicar no kit já marcado
    // abre/fecha as vantagens; marcar outro kit sempre abre o novo. O atalho "Quero N unidades com
    // frete grátis" marca o kit indicado e dispara 'change' pra atualizar variante/preço/total.
    root.querySelectorAll('[data-kits]').forEach((list) => {
      list.addEventListener('click', (event) => {
        const upgrade = event.target.closest('[data-kit-upgrade]');
        if (upgrade) {
          const target = document.getElementById(upgrade.dataset.kitUpgrade);
          if (!target) return;
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
          const kit = target.closest('[data-kit]');
          if (kit) kit.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          return;
        }
        const head = event.target.closest('[data-kit-head]');
        if (!head) return;
        const kit = head.closest('[data-kit]');
        const input = kit && kit.querySelector('input[type="radio"]');
        if (input && input.checked) {
          event.preventDefault();
          kit.classList.toggle('is-collapsed');
        }
      });
      list.addEventListener('change', () => {
        list.querySelectorAll('[data-kit]').forEach((kit) => kit.classList.remove('is-collapsed'));
      });
    });

    // Dica de arrastar da galeria: some na primeira rolagem
    const swipeHint = root.querySelector('[data-swipe-hint]');
    const swipeGallery = root.querySelector('[data-gallery]');
    if (swipeHint && swipeGallery) {
      swipeGallery.addEventListener('scroll', () => swipeHint.classList.add('is-hidden'), { once: true, passive: true });
    }

    // Barra de compra fixa: aparece quando o botão principal sai da tela e some ao chegar no rodapé
    const sticky = root.querySelector('[data-sticky-buy]');
    const mainButton = root.querySelector('[data-add-button]');
    // (calculado na rolagem, não com IntersectionObserver: ele só avisa quando o botão CRUZA a borda da
    // tela, e um pulo de rolagem — âncora, recarregar a página no meio — deixava a barra escondida)
    if (sticky && mainButton) {
      const footer = document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer');
      let ticking = false;
      const update = () => {
        ticking = false;
        const passed = mainButton.getBoundingClientRect().bottom < 0;
        const atFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
        const hide = !passed || atFooter;
        if (sticky.hidden !== hide) sticky.hidden = hide;
      };
      const request = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request, { passive: true });
      update();
    }

    const variantsData = root.querySelector('[data-variants]');
    // Além dos grupos de opção que moram dentro da própria seção "Produto" (blocos), também aceita
    // grupos de uma seção SEPARADA da página (ex.: "Ofertas com foto", sections/kit-offers.liquid)
    // marcados com [data-kit-offers-group] no contêiner que envolve os rádios — mesmo formato de
    // [data-option-group], só que buscado no documento inteiro em vez de escopado à raiz.
    // Quando o box de compra já tem um seletor para a mesma opção (data-option-position igual), o
    // grupo externo NÃO vira um grupo a mais: ele só espelha o interno nos dois sentidos (marcar lá
    // marca aqui e dispara 'change' no interno). Sem seletor interno, o grupo externo é o seletor.
    const internalGroups = Array.from(root.querySelectorAll('[data-option-group]'));
    const externalGroups = [];
    document.querySelectorAll('[data-kit-offers-group]').forEach((external) => {
      const twin = internalGroups.find((group) => group.dataset.optionPosition && group.dataset.optionPosition === external.dataset.optionPosition);
      if (!twin) {
        externalGroups.push(external);
        return;
      }
      const setChecked = (group, value) => {
        const select = group.querySelector('select');
        if (select) {
          if (select.value === value) return false;
          select.value = value;
          return true;
        }
        const input = Array.from(group.querySelectorAll('input[type="radio"]')).find((radio) => radio.value === value);
        if (!input || input.checked) return false;
        input.checked = true;
        return input;
      };
      external.addEventListener('change', (event) => {
        const changed = setChecked(twin, event.target.value);
        if (changed) (changed === true ? twin.querySelector('select') : changed).dispatchEvent(new Event('change', { bubbles: true }));
      });
      twin.addEventListener('change', () => {
        const select = twin.querySelector('select');
        const checked = twin.querySelector('input[type="radio"]:checked');
        const value = select ? select.value : checked && checked.value;
        if (value !== undefined && value !== null) setChecked(external, value);
      });
    });
    const groups = internalGroups.concat(externalGroups);
    if (!variantsData || groups.length === 0) return;

    const variants = JSON.parse(variantsData.textContent);
    const idInput = root.querySelector('[data-variant-id]');
    const addButton = root.querySelector('[data-add-button]');
    const gallery = root.querySelector('[data-gallery]');
    const mobile = window.matchMedia('(max-width: 749px)');

    const selectedValue = (group) => {
      const select = group.querySelector('select');
      if (select) return select.value;
      const checked = group.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : undefined;
    };

    const currentOptions = () => groups.map(selectedValue);

    const findVariant = (options) =>
      variants.find((variant) => variant.options.every((value, index) => value === options[index]));

    const renderPrice = (variant) => {
      currentVariantAmount = variant.amount || 0;
      currentVariantCompareAmount = variant.compareAmount || variant.amount || 0;
      currentVariantTitle = variant.title || '';
      updateTotalPrice();

      // Só os blocos de preço (snippets/price.liquid). Os cards do "compre junto" também têm
      // data-price (valor em centavos para a soma) e não podem ser reescritos aqui.
      root.querySelectorAll('.price[data-price]').forEach((el) => {
        el.classList.toggle('price--on-sale', Boolean(variant.compare));
        el.replaceChildren();

        if (variant.compare) {
          const compare = document.createElement('s');
          compare.className = 'price__compare';
          compare.textContent = variant.compare;
          el.append(compare);
        }

        const current = document.createElement('span');
        current.className = 'price__current';
        current.textContent = variant.price;
        el.append(current);
      });

      root.querySelectorAll('[data-discount]').forEach((el) => {
        const percent = Number(variant.discount);
        el.hidden = !percent;
        if (percent) el.textContent = el.textContent.replace(/\d+/, String(percent));
      });

      root.querySelectorAll('[data-cashback]').forEach((el) => {
        el.innerHTML = variant.cashback || '';
        el.hidden = !variant.cashback;
      });

      root.querySelectorAll('[data-sticky-installments]').forEach((el) => {
        const count = Number(el.dataset.stickyInstallments) || 0;
        if (count < 2 || !variant.amount) return;
        const per = totalFormat.format(Math.round(variant.amount / count) / 100);
        el.textContent = (el.dataset.template || '').replace('[n]', String(count)).replace('[valor]', per);
      });

      root.querySelectorAll('[data-installments]').forEach((el) => {
        el.textContent = variant.installments || '';
        el.hidden = !variant.installments;
      });

    };

    const renderButton = (variant) => {
      const available = Boolean(variant && variant.available);

      if (addButton) {
        addButton.disabled = !available;
        addButton.textContent = available ? addButton.dataset.labelAdd : addButton.dataset.labelSold;
      }
      root.querySelectorAll('[data-kit-cta]').forEach((button) => {
        const kit = button.closest('[data-kit]');
        const input = kit && kit.querySelector('input[type="radio"]');
        if (input && input.checked) button.disabled = !available;
      });
      const stickyButton = root.querySelector('[data-sticky-button]');
      if (stickyButton) stickyButton.disabled = !available;

      const paymentButton = root.querySelector('.shopify-payment-button');
      if (paymentButton) paymentButton.style.display = available ? '' : 'none';
    };

    // Marca (riscado) as opções que não existem ou estão esgotadas para a combinação atual
    const renderAvailability = () => {
      const chosen = currentOptions();

      groups.forEach((group, groupIndex) => {
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const label = group.querySelector(`label[for="${input.id}"]`);
          if (!label) return;

          const options = chosen.map((value, index) => (index === groupIndex ? input.value : value));
          const match = findVariant(options);
          label.classList.toggle('is-unavailable', !match || !match.available);
        });

        const current = group.querySelector('[data-option-current]');
        if (current && chosen[groupIndex] !== undefined) current.textContent = chosen[groupIndex];
      });
    };

    const scrollToMedia = (mediaId) => {
      if (!gallery || !mediaId) return;
      const target = gallery.querySelector(`[data-media-id="${mediaId}"]`);
      if (!target) return;

      if (mobile.matches || gallery.classList.contains('carousel__track')) {
        gallery.scrollTo({ left: target.offsetLeft - gallery.offsetLeft, behavior: 'smooth' });
      } else {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    // Barra fixa: seletor de kit/variante. Marca a mesma opção nos grupos da página e dispara
    // 'change' neles, então todo o resto (preço, total, frete, kits, seção de ofertas) reage igual
    // a um clique no seletor principal.
    const stickyVariant = root.querySelector('[data-sticky-variant]');
    const stickyPanel = root.querySelector('[data-sticky-variant-panel]');
    const stickyToggle = stickyVariant && stickyVariant.querySelector('[data-sticky-variant-toggle]');
    const stickyCurrent = stickyVariant && stickyVariant.querySelector('[data-sticky-variant-current]');
    const setStickyOpen = (open) => {
      if (!stickyPanel || !stickyToggle) return;
      stickyPanel.hidden = !open;
      stickyToggle.setAttribute('aria-expanded', String(open));
      if (open) {
        const checked = stickyPanel.querySelector('[aria-checked="true"]');
        if (checked) checked.focus({ preventScroll: true });
      }
    };
    const syncStickyVariant = (variant) => {
      if (!stickyPanel || !variant) return;
      stickyPanel.querySelectorAll('[data-sticky-variant-option]').forEach((option) => {
        option.setAttribute('aria-checked', String(option.dataset.stickyVariantOption === String(variant.id)));
      });
      if (stickyCurrent) stickyCurrent.textContent = variant.title;
    };
    if (stickyPanel && stickyToggle) {
      stickyToggle.addEventListener('click', () => setStickyOpen(stickyPanel.hidden));
      stickyPanel.addEventListener('click', (event) => {
        const option = event.target.closest('[data-sticky-variant-option]');
        if (!option || option.disabled) return;
        const variant = variants.find((item) => String(item.id) === option.dataset.stickyVariantOption);
        if (!variant) return;
        groups.forEach((group, index) => {
          const value = variant.options[index];
          const select = group.querySelector('select');
          if (select) {
            if (select.value === value) return;
            select.value = value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
          const input = Array.from(group.querySelectorAll('input[type="radio"]')).find((radio) => radio.value === value);
          if (!input || input.checked) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        syncStickyVariant(variant);
        setStickyOpen(false);
        stickyToggle.focus({ preventScroll: true });
      });
      document.addEventListener('click', (event) => {
        if (stickyPanel.hidden || stickyPanel.contains(event.target) || stickyVariant.contains(event.target)) return;
        setStickyOpen(false);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || stickyPanel.hidden) return;
        setStickyOpen(false);
        stickyToggle.focus({ preventScroll: true });
      });
      // A barra some quando o botão principal volta à tela: fecha a lista junto
      if (sticky) new MutationObserver(() => sticky.hidden && !stickyPanel.hidden && setStickyOpen(false)).observe(sticky, { attributes: true, attributeFilter: ['hidden'] });
    }

    const update = () => {
      const variant = findVariant(currentOptions());

      renderAvailability();
      renderButton(variant);
      syncStickyVariant(variant);
      if (!variant) return;

      if (idInput) idInput.value = variant.id;
      renderPrice(variant);
      scrollToMedia(variant.media);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };

    groups.forEach((group) => group.addEventListener('change', update));

    // Mudar a quantidade também precisa recalcular a barra de frete grátis (que é por total, não por unidade).
    const quantityField = root.querySelector('input[name="quantity"]');
    if (quantityField) {
      quantityField.addEventListener('input', () => {
        const variant = findVariant(currentOptions());
        if (variant) renderPrice(variant);
      });
    }

    renderAvailability();
  };

  const boot = (scope = document) => scope.querySelectorAll('[data-product]').forEach(initProduct);
  boot();
  // Editor de temas: a seção redesenhada volta sem os eventos, então inicia de novo
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
